import React from "react";
import { Card, Col, Row, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  NumberOutlined,
  ProfileOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  InternalTaskSummaryData,
  fmtINR,
  fmtINRCompact,
  num,
} from "./internalTaskShared";

// KPI cards, style copied from MachineryKpiCards — compact ₹ value with a
// Tooltip carrying the exact fmtINR amount. Four cards: distinct internal
// task numbers, tasks covered (with the unassigned count surfaced), and the
// planned vs executed ₹ grand totals.

const valueStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: "#1a1a2e",
  marginTop: 6,
  fontVariantNumeric: "tabular-nums",
  cursor: "default",
  width: "fit-content",
};

const titleStyle = (color: string): React.CSSProperties => ({
  fontSize: 12.5,
  fontWeight: 600,
  color,
  display: "flex",
  alignItems: "center",
  gap: 6,
});

const cardStyle = (background: string, border: string): React.CSSProperties => ({
  borderRadius: 12,
  background,
  border: `1px solid ${border}`,
  height: "100%",
});

const subStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#8c8c8c",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const InternalTaskKpiCards: React.FC<{
  data?: InternalTaskSummaryData | null;
  onUnassignedClick?: () => void;
}> = ({ data, onUnassignedClick }) => {
  const groupCount = num(data?.groupCount);
  const taskCount = num(data?.taskCount);
  const unassignedTaskCount = num(data?.unassignedTaskCount);
  const plannedAmount = num(data?.plannedAmount);
  const executedAmount = num(data?.executedAmount);
  const executedPlannedAmount = num(data?.executedPlannedAmount);

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#f0f5ff", "#adc6ff")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#2f54eb")}>
            <NumberOutlined /> Internal Task Numbers
          </div>
          <div style={valueStyle}>{groupCount.toLocaleString("en-IN")}</div>
          <div style={subStyle}>Distinct numbers in scope</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#e6fffb", "#87e8de")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#08979c")}>
            <ProfileOutlined /> Tasks
          </div>
          <div style={valueStyle}>{taskCount.toLocaleString("en-IN")}</div>
          {unassignedTaskCount > 0 ? (
            <Tooltip title="View the tasks without an internal task number">
              <div
                role="button"
                tabIndex={0}
                onClick={onUnassignedClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onUnassignedClick?.();
                }}
                style={{
                  ...subStyle,
                  color: "#d46b08",
                  cursor: onUnassignedClick ? "pointer" : "default",
                  textDecoration: onUnassignedClick ? "underline" : "none",
                  textUnderlineOffset: 2,
                  width: "fit-content",
                }}
              >
                {unassignedTaskCount.toLocaleString("en-IN")} unassigned →
              </div>
            </Tooltip>
          ) : (
            <div style={subStyle}>All carry an internal number</div>
          )}
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#f6ffed", "#b7eb8f")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#389e0d")}>
            <WalletOutlined /> Planned Amount
          </div>
          <Tooltip title={fmtINR(plannedAmount)}>
            <div style={valueStyle}>{fmtINRCompact(plannedAmount)}</div>
          </Tooltip>
          <div style={subStyle}>Σ Task Qty × Planned Rate</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#fff7e6", "#ffd591")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#fa8c16")}>
            <CheckCircleOutlined /> Executed Amount
          </div>
          <Tooltip title={fmtINR(executedAmount)}>
            <div style={valueStyle}>{fmtINRCompact(executedAmount)}</div>
          </Tooltip>
          <div style={subStyle}>
            {plannedAmount > 0
              ? `${((executedAmount / plannedAmount) * 100).toFixed(1)}% of planned`
              : "—"}
          </div>
          <Tooltip title={fmtINR(executedPlannedAmount)}>
            <div style={{ ...subStyle, width: "fit-content" }}>
              3M (Exec-Planned): {fmtINRCompact(executedPlannedAmount)}
            </div>
          </Tooltip>
        </Card>
      </Col>
    </Row>
  );
};

export default InternalTaskKpiCards;
